import { lt } from './localized'
import { createStarterSkills, grantSkillXp, unlockSkill } from './skills'
import type { PortraitKey } from './portraits'
import type { SkillKey, Survivor, SurvivorGender, SurvivorRole } from './types'

/** Fixed radio contacts — one row per portrait asset so faces stay unique in the shelter. */
export type BroadcastRecruitTemplate = {
  /** Matches `Survivor.portrait` / keys in `PORTRAIT_SRCS` (human portraits only). */
  portraitKey: PortraitKey
  name: string
  role: SurvivorRole
  gender: SurvivorGender
  /** Extra skill unlocked and boosted for this contact. */
  highlightSkill: SkillKey
  highlightXp: number
  bio: ReturnType<typeof lt>
}

export const BROADCAST_RECRUIT_POOL: BroadcastRecruitTemplate[] = [
  {
    portraitKey: 'male_01',
    name: 'Elias',
    role: 'Scavenger',
    gender: 'male',
    highlightSkill: 'electronics',
    highlightXp: 42,
    bio: lt(
      'Repareerde walkietalkies vóór de val. Hoorde je signaal op de eerste dag.',
      'Fixed walkie-talkies before the fall. Picked up your signal on day one.',
    ),
  },
  {
    portraitKey: 'male_02',
    name: 'Sven',
    role: 'Medic',
    gender: 'male',
    highlightSkill: 'first_aid',
    highlightXp: 48,
    bio: lt(
      'Werkte op een huisartsenpost. Wil zijn handschoenen niet verspillen.',
      'Worked at a GP clinic. Does not want to waste a pair of gloves.',
    ),
  },
  {
    portraitKey: 'male_04',
    name: 'Daan',
    role: 'Scavenger',
    gender: 'male',
    highlightSkill: 'tinkering',
    highlightXp: 44,
    bio: lt(
      'Improviseerde sloten van schroot. Zoekt een plek waar gereedschap telt.',
      'Improvised locks from scrap. Looking for a place where tools matter.',
    ),
  },
  {
    portraitKey: 'male_05',
    name: 'Finn',
    role: 'Cook',
    gender: 'male',
    highlightSkill: 'cooking',
    highlightXp: 46,
    bio: lt(
      'Kookte voor een buurthuis. Ruikt nog steeds naar uien en hoop.',
      'Cooked for a community centre. Still smells of onions and hope.',
    ),
  },
  {
    portraitKey: 'male_06',
    name: 'Ruben',
    role: 'Scavenger',
    gender: 'male',
    highlightSkill: 'climbing',
    highlightXp: 40,
    bio: lt(
      'Wist elke steiger in de haven. Nu volgt hij alleen nog jouw stem.',
      'Knew every jetty in the harbour. Now he only follows your voice.',
    ),
  },
  {
    portraitKey: 'female_01',
    name: 'Nora',
    role: 'Medic',
    gender: 'female',
    highlightSkill: 'first_aid',
    highlightXp: 45,
    bio: lt(
      'Rookte verband aan op de middelbare school toneel. Nu voor echt.',
      'Burned through bandages in school plays. Now for real.',
    ),
  },
  {
    portraitKey: 'female_02',
    name: 'Mila',
    role: 'Cook',
    gender: 'female',
    highlightSkill: 'cooking',
    highlightXp: 50,
    bio: lt(
      'Bracht soep naar buren met een karretje. Wil weer voor meer dan twee koken.',
      'Wheeled soup to neighbours. Wants to cook for more than two again.',
    ),
  },
  {
    portraitKey: 'female_03',
    name: 'Iris',
    role: 'Scavenger',
    gender: 'female',
    highlightSkill: 'linguistics',
    highlightXp: 38,
    bio: lt(
      'Leerde vijf talen voor de lol. Nu onderhandelt ze met statische ruis.',
      'Learned five languages for fun. Now she negotiates with static.',
    ),
  },
]

function portraitsInUse(
  roster: Survivor[],
  pendingOffers: Survivor[],
): Set<string> {
  const used = new Set<string>()
  for (const s of roster) {
    if (s.portrait) used.add(s.portrait)
  }
  for (const s of pendingOffers) {
    if (s.portrait) used.add(s.portrait)
  }
  return used
}

/**
 * Picks a random unused template and materializes a fresh survivor id.
 * Returns null when every portrait in the pool is already on the roster or in pending offers.
 */
export function pickBroadcastRecruitSurvivor(
  roster: Survivor[],
  pendingOffers: Survivor[],
): Survivor | null {
  const blocked = portraitsInUse(roster, pendingOffers)
  const available = BROADCAST_RECRUIT_POOL.filter((t) => !blocked.has(t.portraitKey))
  if (available.length === 0) return null

  const template = available[Math.floor(Math.random() * available.length)]!
  const id = `radio-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

  let skills = unlockSkill(createStarterSkills(), template.highlightSkill)
  skills = grantSkillXp(skills, template.highlightSkill, template.highlightXp)

  return {
    id,
    name: template.name,
    role: template.role,
    status: 'Idle',
    gender: template.gender,
    portrait: template.portraitKey,
    needs: { hunger: 85, thirst: 85, energy: 72 },
    skills,
    loadout: { weapon: null, tool: null },
    bio: template.bio,
  }
}
